/**
 * Data size converter — pure, deterministic byte-unit conversion.
 * Decimal units (KB = 1000 bytes) and binary units (KiB = 1024 bytes).
 * Safe to render server-side.
 */

export interface SizeUnit {
  id: string;
  /** English display label. */
  label: string;
  /** Size of one unit in bytes. */
  bytes: number;
}

export const UNITS: readonly SizeUnit[] = [
  { id: "B", label: "Bytes (B)", bytes: 1 },
  { id: "KB", label: "Kilobytes (KB)", bytes: 1e3 },
  { id: "MB", label: "Megabytes (MB)", bytes: 1e6 },
  { id: "GB", label: "Gigabytes (GB)", bytes: 1e9 },
  { id: "TB", label: "Terabytes (TB)", bytes: 1e12 },
  { id: "KiB", label: "Kibibytes (KiB)", bytes: 1024 },
  { id: "MiB", label: "Mebibytes (MiB)", bytes: 1024 ** 2 },
  { id: "GiB", label: "Gibibytes (GiB)", bytes: 1024 ** 3 },
  { id: "TiB", label: "Tebibytes (TiB)", bytes: 1024 ** 4 },
];

const BY_ID: Record<string, SizeUnit> = Object.fromEntries(
  UNITS.map((u) => [u.id, u]),
);

/** Convert `value` of `from` units into `to` units, or null if invalid. */
export function convert(
  value: number,
  from: string,
  to: string,
): number | null {
  if (!Number.isFinite(value)) return null;
  if (!Object.hasOwn(BY_ID, from) || !Object.hasOwn(BY_ID, to)) return null;
  return (value * BY_ID[from].bytes) / BY_ID[to].bytes;
}

export interface SizeResult {
  id: string;
  label: string;
  value: number;
}

/** Convert `value` of `from` units into every supported unit. */
export function convertAll(value: number, from: string): SizeResult[] | null {
  if (!Number.isFinite(value) || !Object.hasOwn(BY_ID, from)) return null;
  const bytes = value * BY_ID[from].bytes;
  return UNITS.map((u) => ({
    id: u.id,
    label: u.label,
    value: bytes / u.bytes,
  }));
}
